import { describeApiError, type PlatformSettings, type RatingFrequency } from '@brush/api-client';
import { ErrorNotice } from '@brush/ui';
import { Settings, Shield } from 'lucide-react';
import { usePatchPlatformSettings } from '../queries';

const FREQUENCIES: { value: RatingFrequency; label: string; desc: string }[] = [
  { value: 'realtime', label: 'Real-time', desc: 'Recalculated on every result upload' },
  { value: 'daily', label: 'Daily', desc: 'Recalculated once per day' },
  { value: 'weekly', label: 'Weekly', desc: 'Recalculated once per week' },
  { value: 'monthly', label: 'Monthly', desc: 'Recalculated once per month' },
];

export function PlatformSettingsPanel({ settings }: { settings: PlatformSettings }) {
  const patch = usePatchPlatformSettings();
  const current = settings.defaultRatingFrequency;

  return (
    <section className="overflow-hidden rounded-xl border border-ink-800/10 bg-white shadow-sm">
      <header className="border-b border-ink-800/10 px-6 py-4">
        <h2 className="flex items-center gap-2 font-display font-semibold text-ink-950">
          <Settings className="size-4 text-ink-800/60" /> Platform settings
        </h2>
        <p className="mt-1 text-xs text-ink-800/60">
          Settings that apply across every room on the platform.
        </p>
      </header>

      <div className="p-6">
        <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-ink-950">
          <Shield className="size-3.5 text-gold-500" /> Default rating calculation frequency
        </div>
        <p className="mb-3 text-xs text-ink-800/60">
          Applied to every room&apos;s default rating. Rooms can&apos;t override it.
        </p>
        <ErrorNotice message={describeApiError(patch.error)} className="mb-3" />

        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {FREQUENCIES.map((f) => {
            const active = current === f.value;
            return (
              <button
                key={f.value}
                type="button"
                onClick={() => patch.mutate({ defaultRatingFrequency: f.value })}
                disabled={patch.isPending}
                className={
                  active
                    ? 'rounded-lg border border-felt-700 bg-felt-700 p-3 text-left text-white transition disabled:opacity-70'
                    : 'rounded-lg border border-ink-800/15 p-3 text-left text-ink-800 transition hover:bg-felt-50/50 disabled:opacity-50'
                }
              >
                <div className="text-sm font-semibold">{f.label}</div>
                <div className={active ? 'mt-0.5 text-xs text-white/70' : 'mt-0.5 text-xs text-ink-800/60'}>
                  {f.desc}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
